class Data4SEOService {
  constructor() {
    this.baseUrl = import.meta.env.VITE_DATA4SEO_API_URL
    if (!this.baseUrl) {
      throw new Error('DATA4SEO_API_URL environment variable is not set')
    }

    const apiKey = import.meta.env.VITE_DATA4SEO_API_KEY
    if (!apiKey) {
      throw new Error('DATA4SEO_API_KEY environment variable is not set')
    }

    const [login, password] = apiKey.split(':')
    if (!login || !password) {
      throw new Error('DATA4SEO_API_KEY must be in format "email:password"')
    }

    this.authHeader = 'Basic ' + btoa(login + ':' + password)
  }

  // Convert competition level string to numeric value for consistent handling
  getCompetitionLevel(level) {
    switch (level?.toUpperCase()) {
      case 'LOW': return 1
      case 'MEDIUM': return 2
      case 'HIGH': return 3
      default: return 2 // Default to MEDIUM if unknown
    }
  }

  // Map keyword data to our format
  mapKeywordData(item, isSeed = false) {
    if (import.meta.env.MODE === 'development') {
      console.log('🗺️ Mapping keyword data:', {
        term: item.keyword || item.seed_keyword,
        isSeed
      })
    }

    const keywordInfo = isSeed ? item.seed_keyword_data?.keyword_info : item.keyword_info
    const impressionsInfo = isSeed ? item.seed_keyword_data?.impressions_info : null
    if (!keywordInfo) return null

    const competitionLevel = this.getCompetitionLevel(keywordInfo.competition_level)

    if (import.meta.env.MODE === 'development') {
      console.log('🗺️ Mapping keyword data:', {
        term: isSeed ? item.seed_keyword : item.keyword,
        keywordInfo,
        searchIntent: item.search_intent,
        isSeed
      })
      
      console.log('🎯 Search intent structure:', {
        term: isSeed ? item.seed_keyword : item.keyword,
        raw: item.search_intent,
        processed: item.search_intent
      })
    }

    // Ensure search intent has the correct structure
    const searchIntent = item.search_intent || { primary: null, secondary: [] }
    if (import.meta.env.MODE === 'development') {
      console.log('🎯 Search intent structure:', {
        primary: searchIntent.primary,
        secondary: searchIntent.secondary
      })
    }
    console.log('🎯 Search intent structure:', {
      term: isSeed ? item.seed_keyword : item.keyword,
      raw: item.search_intent,
      processed: searchIntent
    })

    const mappedData = {
      term: isSeed ? item.seed_keyword : item.keyword,
      searchVolume: keywordInfo.search_volume || 0,
      cpc: keywordInfo.cpc || 0,
      difficulty: {
        competitionLevel,
        competitionLevelText: keywordInfo.competition_level || 'MEDIUM',
        competition: keywordInfo.competition || 0,
        keywordDifficulty: item.keyword_data?.difficulty_metrics?.keyword_difficulty || keywordInfo.keyword_difficulty || 0
      },
      searchIntent,
      monthlySearches: item.monthly_searches || keywordInfo.monthly_searches || [],
      searchTrends: {
        monthly: item.search_trends?.monthly || keywordInfo.search_volume_trend?.monthly || 0,
        quarterly: item.search_trends?.quarterly || keywordInfo.search_volume_trend?.quarterly || 0,
        yearly: item.search_trends?.yearly || keywordInfo.search_volume_trend?.yearly || 0
      },
      serpData: {
        etv: keywordInfo.estimated_traffic_volume || 0,
        avgBacklinks: item.serp_info?.avg_backlinks_count || 0,
        avgDomainAge: item.serp_info?.avg_domain_age || 0,
        avgPageScore: item.serp_info?.avg_page_score || 0,
        competitorCount: item.serp_info?.competitor_count || 0,
        keywordDifficulty: item.keyword_info?.keyword_difficulty || keywordInfo.keyword_difficulty || 0,
        competitorMetrics: {
          avgTitle: item.competitor_metrics?.avg_title_length || item.serp_info?.avg_title_length || 0,
          avgDescription: item.competitor_metrics?.avg_description_length || item.serp_info?.avg_description_length || 0,
          topDomains: item.competitor_metrics?.top_domains || item.serp_info?.top_domains || []
        }
      },
      impressions: impressionsInfo ? {
        lastUpdated: impressionsInfo.last_updated_time,
        adPosition: {
          min: impressionsInfo.ad_position_min,
          max: impressionsInfo.ad_position_max,
          average: impressionsInfo.ad_position_average
        },
        cpc: {
          min: impressionsInfo.cpc_min,
          max: impressionsInfo.cpc_max,
          average: impressionsInfo.cpc_average
        },
        dailyImpressions: {
          min: impressionsInfo.daily_impressions_min,
          max: impressionsInfo.daily_impressions_max,
          average: impressionsInfo.daily_impressions_average
        },
        dailyClicks: {
          min: impressionsInfo.daily_clicks_min,
          max: impressionsInfo.daily_clicks_max,
          average: impressionsInfo.daily_clicks_average
        },
        dailyCost: {
          min: impressionsInfo.daily_cost_min,
          max: impressionsInfo.daily_cost_max,
          average: impressionsInfo.daily_cost_average
        }
      } : null,
      isSeedKeyword: isSeed,
      isOpportunity: (
        competitionLevel === 1 && // LOW competition
        (keywordInfo.competition || 0) <= 0.25 && // 25% competition or less
        (keywordInfo.search_volume || 0) >= 100 // At least 100 monthly searches
      )
    }

    if (import.meta.env.MODE === 'development') {
      console.log('🗺️ Mapped data result:', {
        term: mappedData.term,
        searchIntent: mappedData.searchIntent,
        isSeed
      })
    }

    return mappedData
  }

  async analyzeSeedKeyword(keyword, locationCode = 2840, languageCode = 'en') {
    try {
      if (import.meta.env.MODE === 'development') {
        console.log('🔍 Analyzing keyword:', { keyword, locationCode, languageCode })
      }
      
      // First get keyword data for search volume and difficulty metrics
      const keywordDataEndpoint = `${this.baseUrl}/v3/keywords_data/google/search_volume/live`
      const keywordDataBody = [{
        "keywords": [keyword],
        "language_code": languageCode,
        "location_code": locationCode,
        "tag": "keyword_analysis"
      }]

      if (import.meta.env.MODE === 'development') {
        console.log('📊 Fetching keyword data...')
      }
      const keywordDataResponse = await fetch(keywordDataEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': this.authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(keywordDataBody)
      })

      const keywordData = await keywordDataResponse.json()
      if (import.meta.env.MODE === 'development') {
        console.log('📊 Keyword data response:', keywordData)
      }

      // Don't throw on keyword data failure, we can still use suggestions
      const keywordDataResult = keywordData.status_code === 20000 ? keywordData.tasks?.[0]?.result?.[0] : null

      // Get search intent data
      const searchIntentEndpoint = `${this.baseUrl}/v3/dataforseo_labs/google/search_intent/live`
      const searchIntentBody = [{
        "keywords": [keyword],
        "language_code": languageCode,
        "tag": "search_intent"
      }]

      if (import.meta.env.MODE === 'development') {
        console.log('🎯 Fetching search intent...', { searchIntentBody })
      }
      const searchIntentResponse = await fetch(searchIntentEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': this.authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(searchIntentBody)
      })

      const searchIntentData = await searchIntentResponse.json()
      if (import.meta.env.MODE === 'development') {
        console.log('🎯 Search intent response:', {
          status: searchIntentData.status_code,
          message: searchIntentData.status_message,
          tasks: searchIntentData.tasks,
          items: searchIntentData.tasks?.[0]?.result?.[0]?.items
        })
      }

      // Then get keyword suggestions
      const suggestionsEndpoint = `${this.baseUrl}/v3/dataforseo_labs/google/keyword_suggestions/live`
      const suggestionsBody = [{
        "keyword": keyword,
        "language_code": languageCode,
        "location_code": locationCode,
        "include_seed_keyword": true,
        "include_serp_info": true,
        "include_impressions_info": true,
        "include_related_keywords": true,
        "sort_by": "relevance",
        "filters": [
          ["keyword_info.search_volume", ">", "0"],
          "and",
          ["keyword_info.cpc", ">=", "0"]
        ],
        "limit": 50
      }]

      if (import.meta.env.MODE === 'development') {
        console.log('🔍 Fetching suggestions...')
      }
      const suggestionsResponse = await fetch(suggestionsEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': this.authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(suggestionsBody)
      })

      const suggestionsData = await suggestionsResponse.json()

      if (suggestionsData.status_code !== 20000) {
        if (import.meta.env.MODE === 'development') {
          console.error('API Error:', {
            status_code: suggestionsData.status_code,
            status_message: suggestionsData.status_message,
            tasks: suggestionsData.tasks
          })
        }
        throw new Error(suggestionsData.status_message || 'API request failed')
      }

      const suggestionsResult = suggestionsData.tasks?.[0]?.result?.[0]
      
      // Check for no results
      if (!suggestionsResult || !suggestionsResult.items || suggestionsResult.items.length === 0) {
        throw new Error(`No keyword data found for "${keyword}". This might be because the keyword has no search volume or is not recognized.`)
      }

      // Get search intent data for the seed keyword
      const seedKeywordIntent = searchIntentData.tasks?.[0]?.result?.[0]?.items?.[0]
      console.log('🎯 Seed keyword intent:', {
        keyword,
        intent: seedKeywordIntent?.keyword_intent,
        secondaryIntents: seedKeywordIntent?.secondary_keyword_intents
      })

      const searchIntentMap = new Map()
      if (seedKeywordIntent?.keyword_intent) {
        const { label: primaryIntent, probability: primaryProb } = seedKeywordIntent.keyword_intent
        const secondaryIntents = (seedKeywordIntent.secondary_keyword_intents || [])
          .filter(({ probability }) => probability > 0.3) // Only include significant secondary intents
          .map(({ label }) => label)
        
        console.log('🎯 Mapped seed keyword intent:', {
          keyword,
          primaryIntent,
          primaryProb,
          secondaryIntents
        })

        searchIntentMap.set(keyword, {
          primary: primaryIntent,
          secondary: secondaryIntents
        })
      }

      // Get difficulty data for the seed keyword if available
      const seedKeywordDifficulty = keywordDataResult?.items?.[0]?.keyword_data?.difficulty_metrics?.keyword_difficulty || 0

      // Map seed keyword data with difficulty from keyword data endpoint (if available)
      const seedKeywordData = this.mapKeywordData({
        seed_keyword: keyword,
        seed_keyword_data: suggestionsResult.seed_keyword_data,
        search_intent: searchIntentMap.get(keyword) || { primary: null, secondary: [] }
      }, true)

      if (import.meta.env.MODE === 'development') {
        console.log('🎯 Mapped seed keyword data:', {
          term: seedKeywordData.term,
          searchIntent: seedKeywordData.searchIntent
        })
      }

      // Map suggestion items
      const mappedItems = suggestionsResult.items.map(item => this.mapKeywordData(item))

      // Get difficulty data for all suggestion keywords
      const suggestionKeywords = suggestionsResult.items.map(item => item.keyword)
      if (!suggestionKeywords.length) {
        throw new Error(`No related keywords found for "${keyword}". Try a different search term.`)
      }

      // Get search intent data for suggestion keywords
      const suggestionIntentBody = [{
        "keywords": suggestionKeywords,
        "language_code": languageCode,
        "tag": "suggestion_intents"
      }]

      if (import.meta.env.MODE === 'development') {
        console.log('🎯 Fetching suggestion intents...', {
          keywordCount: suggestionKeywords.length,
          sampleKeywords: suggestionKeywords.slice(0, 3)
        })
      }

      const suggestionIntentResponse = await fetch(searchIntentEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': this.authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(suggestionIntentBody)
      })

      const suggestionIntentData = await suggestionIntentResponse.json()
      if (import.meta.env.MODE === 'development') {
        console.log('🎯 Suggestion intents response:', {
          status: suggestionIntentData.status_code,
          message: suggestionIntentData.status_message,
          itemCount: suggestionIntentData.tasks?.[0]?.result?.[0]?.items?.length
        })
      }

      const suggestionIntents = suggestionIntentData.tasks?.[0]?.result?.[0]?.items || []
      
      // Create a map of keyword to search intent data
      suggestionIntents.forEach(item => {
        if (item?.keyword_intent) {
          const { label: primaryIntent, probability: primaryProb } = item.keyword_intent
          const secondaryIntents = (item.secondary_keyword_intents || [])
            .filter(({ probability }) => probability > 0.3) // Only include significant secondary intents
            .map(({ label }) => label)
          
          if (import.meta.env.MODE === 'development') {
            console.log('🎯 Mapped suggestion intent:', {
              keyword: item.keyword,
              primaryIntent,
              primaryProb,
              secondaryIntents
            })
          }

          searchIntentMap.set(item.keyword, {
            primary: primaryIntent,
            secondary: secondaryIntents
          })
        }
      })

      const suggestionDifficultyBody = [{
        "keywords": suggestionKeywords,
        "language_code": languageCode,
        "location_code": locationCode,
        "include_serp_info": true,
        "include_difficulty_metrics": true
      }]

      const suggestionDifficultyResponse = await fetch(`${this.baseUrl}/v3/keywords_data/google/search_volume/live`, {
        method: 'POST',
        headers: {
          'Authorization': this.authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(suggestionDifficultyBody)
      })

      const suggestionDifficultyData = await suggestionDifficultyResponse.json()
      const difficultyMap = new Map(
        suggestionDifficultyData.tasks?.[0]?.result?.[0]?.items?.map(item => 
          [item.keyword, item.keyword_data?.difficulty_metrics?.keyword_difficulty || 0]
        ) || []
      )

      // Map suggestions with their difficulty data and search intent
      const suggestions = mappedItems
        .map((item, index) => ({
          ...item,
          difficulty: {...item.difficulty, keywordDifficulty: difficultyMap.get(suggestionKeywords[index]) || 0},
          searchIntent: searchIntentMap.get(item.term) || {
            primary: null,
            secondary: []
          }
        }))
        .filter(item => item !== null)

      if (!suggestions.length) {
        throw new Error(`Could not process any related keywords for "${keyword}". Try a different search term.`)
      }

      return [seedKeywordData, ...suggestions]
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        console.error('Error analyzing seed keyword:', error)
      }
      throw error
    }
  }

  async getLocations() {
    try {
      const response = await fetch(`${this.baseUrl}/v3/dataforseo_labs/locations_and_languages`, {
        method: 'GET',
        headers: {
          'Authorization': this.authHeader
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      if (!data.tasks?.[0]?.result) {
        throw new Error('No location data found')
      }

      // Transform the data into a more usable format
      return data.tasks[0].result.map(location => ({
        location_code: location.location_code,
        location_name: location.location_name,
        location_type: location.location_type,
        country_iso_code: location.country_iso_code,
        is_country: location.location_type === 'Country'
      })).sort((a, b) => {
        // Sort countries first, then other locations
        if (a.is_country && !b.is_country) return -1
        if (!a.is_country && b.is_country) return 1
        return a.location_name.localeCompare(b.location_name)
      })
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        console.error('Error fetching locations:', error)
      }
      throw error
    }
  }

  async getLanguages() {
    try {
      const response = await fetch(`${this.baseUrl}/v3/dataforseo_labs/locations_and_languages`, {
        method: 'GET',
        headers: {
          'Authorization': this.authHeader
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      if (!data.tasks?.[0]?.result) {
        throw new Error('No language data found')
      }

      return data.tasks[0].result
        .filter(lang => lang.language_name && lang.language_code)
        .map(lang => ({
          language_code: lang.language_code,
          language_name: lang.language_name
        }))
        .sort((a, b) => a.language_name.localeCompare(b.language_name))
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        console.error('Error fetching languages:', error)
      }
      throw error
    }
  }
}

export const data4seoService = {
  analyzeSeedKeyword: new Data4SEOService().analyzeSeedKeyword.bind(new Data4SEOService()),
  getLocations: new Data4SEOService().getLocations.bind(new Data4SEOService()),
  getLanguages: new Data4SEOService().getLanguages.bind(new Data4SEOService())
}
